// src/pages/EnquiryForm.jsx
// Contact Us page RFQ form. Collects name, company, mobile, email, city and
// the requirement, and saves it to the `enquiries` Firestore collection --
// tixaru's Sales > Website Leads > Contact Us tab reads that collection
// live (biba-erp: backend/app/services/website_leads.py), so the field
// names here are the contract: name / company / phone / email / city /
// requirement / status / createdAt. City became mandatory on 2026-09-06;
// older enquiries have no city.
import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import { db } from "../firebase";
import { reportConversion } from "../utils/googleAdsConversion";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_REQUIREMENT_LENGTH = 10;

const EMPTY_FORM = {
  name: "",
  company: "",
  phone: "",
  email: "",
  city: "",
  requirement: "",
  // Honeypot -- hidden from people, filled in by naive bots. Anything in
  // here silently drops the submission.
  website: "",
};

// The phone-number library is large (~130 KB), so it is loaded when the form is
// submitted rather than shipped in the main bundle for every page.
const validate = (form, isValidPhoneNumber) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Please enter your name.";
  if (!form.company.trim()) errors.company = "Please enter your company name.";

  const phone = form.phone.trim();
  if (!phone) {
    errors.phone = "Please enter your mobile number.";
  } else if (!isValidPhoneNumber(phone, "IN")) {
    errors.phone = "Please enter a valid mobile number (add the country code if outside India).";
  }

  const email = form.email.trim();
  if (!email) {
    errors.email = "Please enter your email address.";
  } else if (!EMAIL_RE.test(email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (!form.city.trim()) errors.city = "Please enter your city.";

  const requirement = form.requirement.trim();
  if (!requirement) {
    errors.requirement = "Please describe your requirement.";
  } else if (requirement.length < MIN_REQUIREMENT_LENGTH) {
    errors.requirement = "Please add a little more detail so we can quote accurately.";
  }
  return errors;
};

// Store phones in E.164 (+91XXXXXXXXXX) so the sales team gets a
// dial-able number regardless of how the visitor typed it.
const normalisePhone = (raw, parsePhoneNumberFromString) => {
  const parsed = parsePhoneNumberFromString(raw.trim(), "IN");
  return parsed ? parsed.number : raw.trim();
};

const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const setField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (form.website) {
      // Bot hit the honeypot -- pretend it worked, write nothing.
      setSubmitted(true);
      return;
    }

    let phoneLib;
    try {
      phoneLib = await import("libphonenumber-js");
    } catch (err) {
      console.error("Could not load phone validation:", err);
      setSubmitError("Please check your connection and try again, or email info@bibaelectronics.com.");
      return;
    }
    const nextErrors = validate(form, phoneLib.isValidPhoneNumber);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "enquiries"), {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: normalisePhone(form.phone, phoneLib.parsePhoneNumberFromString),
        email: form.email.trim().toLowerCase(),
        city: form.city.trim(),
        requirement: form.requirement.trim(),
        status: "new",
        source: "contact-page",
        page: window.location.pathname,
        createdAt: serverTimestamp(),
      });
      reportConversion("enquiryFormSubmitted", {
        email: form.email.trim(),
        phone: normalisePhone(form.phone, phoneLib.parsePhoneNumberFromString),
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error("Enquiry submission error:", err);
      setSubmitError(
        "We couldn't send your enquiry just now. Please try again, or email info@bibaelectronics.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="cp-form-card cp-form-success" role="status" aria-live="polite">
        <FaCheckCircle className="cp-form-success-icon" />
        <h3 className="cp-form-success-title">Thank you, we&apos;ve received your enquiry.</h3>
        <p className="cp-form-success-desc">
          Our sales team will review your requirement and get back to you with a quotation within one
          business day.
        </p>
        <button type="button" className="cp-form-again" onClick={() => setSubmitted(false)}>
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form className="cp-form-card" onSubmit={handleSubmit} noValidate>
      <div className="cp-form-head">
        <h3 className="cp-form-title">Request a Quotation</h3>
        <p className="cp-form-sub">Fill in the details below and we&apos;ll get back to you shortly.</p>
      </div>

      {submitError && (
        <div className="cp-form-alert" role="alert">
          {submitError}
        </div>
      )}

      <div className="cp-form-grid">
        <div className={`cp-field ${errors.name ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-name">Name *</label>
          <input
            id="rfq-name"
            type="text"
            autoComplete="name"
            placeholder="Your full name"
            value={form.name}
            onChange={setField("name")}
            aria-invalid={!!errors.name}
          />
          {errors.name && <span className="cp-field-error">{errors.name}</span>}
        </div>

        <div className={`cp-field ${errors.company ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-company">Company *</label>
          <input
            id="rfq-company"
            type="text"
            autoComplete="organization"
            placeholder="Your company name"
            value={form.company}
            onChange={setField("company")}
            aria-invalid={!!errors.company}
          />
          {errors.company && <span className="cp-field-error">{errors.company}</span>}
        </div>

        <div className={`cp-field ${errors.phone ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-phone">Mobile Number *</label>
          <input
            id="rfq-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+91 98765 43210"
            value={form.phone}
            onChange={setField("phone")}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && <span className="cp-field-error">{errors.phone}</span>}
        </div>

        <div className={`cp-field ${errors.email ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-email">Email *</label>
          <input
            id="rfq-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={form.email}
            onChange={setField("email")}
            aria-invalid={!!errors.email}
          />
          {errors.email && <span className="cp-field-error">{errors.email}</span>}
        </div>

        <div className={`cp-field cp-field--full ${errors.city ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-city">City *</label>
          <input
            id="rfq-city"
            type="text"
            autoComplete="address-level2"
            placeholder="Your city"
            value={form.city}
            onChange={setField("city")}
            aria-invalid={!!errors.city}
          />
          {errors.city && <span className="cp-field-error">{errors.city}</span>}
        </div>

        <div className={`cp-field cp-field--full ${errors.requirement ? "cp-field--error" : ""}`}>
          <label htmlFor="rfq-requirement">Requirement *</label>
          <textarea
            id="rfq-requirement"
            rows={5}
            placeholder="Tell us what you need: product or part number, size, interface, quantity, target price, timeline..."
            value={form.requirement}
            onChange={setField("requirement")}
            aria-invalid={!!errors.requirement}
          />
          {errors.requirement && <span className="cp-field-error">{errors.requirement}</span>}
        </div>

        {/* Honeypot: off-screen, not tabbable, ignored by autofill. */}
        <div className="cp-field cp-field--hp" aria-hidden="true">
          <label htmlFor="rfq-website">Website</label>
          <input
            id="rfq-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={setField("website")}
          />
        </div>
      </div>

      <div className="cp-form-foot">
        <button type="submit" className="cp-form-submit" disabled={submitting}>
          <FaPaperPlane className="cp-form-submit-icon" />
          {submitting ? "Sending..." : "Submit Enquiry"}
        </button>
        <p className="cp-form-privacy">
          We only use these details to respond to your enquiry. See our{" "}
          <a href="/about/privacy-policy">privacy policy</a>.
        </p>
      </div>
    </form>
  );
};

export default EnquiryForm;
